import { create } from 'zustand';
import { LLMProvider, ApiKeyInfo, ModelSettings } from '../types';
import apiClient from '../api/client';

/**
 * Settings Store
 * Manages API keys and model settings
 * Validates: Requirements 12.1, 12.3, 12.5, 21.1, 21.2, 21.3, 21.4
 */

interface SettingsState {
  // API Keys state
  apiKeys: ApiKeyInfo[];
  apiKeysLoading: boolean;
  apiKeysError: string | null;
  
  // Model settings state
  modelSettings: Record<string, ModelSettings>;
  defaultModel: string | null;
  settingsLoading: boolean;
  settingsError: string | null;
  
  // Actions
  fetchApiKeys: () => Promise<void>;
  addApiKey: (provider: LLMProvider, apiKey: string, validate?: boolean) => Promise<boolean>;
  deleteApiKey: (provider: LLMProvider) => Promise<boolean>;
  validateApiKey: (provider: LLMProvider) => Promise<boolean>;
  
  fetchModelSettings: () => Promise<void>;
  updateModelSettings: (model: string, settings: ModelSettings) => Promise<boolean>;
  setDefaultModel: (model: string) => Promise<boolean>;
  resetModelSettings: (model: string) => Promise<boolean>;
  
  // Helper to check if provider has key configured
  hasApiKey: (provider: LLMProvider) => boolean;
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  // Initial state
  apiKeys: [],
  apiKeysLoading: false,
  apiKeysError: null,
  
  modelSettings: {},
  defaultModel: null,
  settingsLoading: false,
  settingsError: null,
  
  /**
   * Fetch all API keys for the current user
   * Validates: Requirements 12.3
   */
  fetchApiKeys: async () => {
    set({ apiKeysLoading: true, apiKeysError: null });
    
    try {
      const response = await apiClient.get<{ success: boolean; data: ApiKeyInfo[] }>('/api/keys');
      set({ 
        apiKeys: response.data.data,
        apiKeysLoading: false,
      });
    } catch (error) {
      const message = getErrorMessage(error);
      set({ 
        apiKeysError: message,
        apiKeysLoading: false,
      });
    }
  },
  
  /**
   * Add a new API key for a provider
   * Validates: Requirements 12.1
   */
  addApiKey: async (provider: LLMProvider, apiKey: string, validate = true): Promise<boolean> => {
    set({ apiKeysLoading: true, apiKeysError: null });
    
    try {
      await apiClient.post('/api/keys', {
        provider,
        apiKey,
        validate,
      });
      
      // Refresh the list
      await get().fetchApiKeys();
      return true;
    } catch (error) {
      const message = getErrorMessage(error);
      set({ 
        apiKeysError: message,
        apiKeysLoading: false,
      });
      return false;
    }
  },
  
  /**
   * Delete an API key for a provider
   * Validates: Requirements 12.4
   */
  deleteApiKey: async (provider: LLMProvider): Promise<boolean> => {
    set({ apiKeysLoading: true, apiKeysError: null });
    
    try {
      await apiClient.delete(`/api/keys/${provider}`);
      
      // Refresh the list
      await get().fetchApiKeys();
      return true;
    } catch (error) {
      const message = getErrorMessage(error);
      set({ 
        apiKeysError: message,
        apiKeysLoading: false,
      });
      return false;
    }
  },
  
  /**
   * Validate an existing API key
   * Validates: Requirements 12.6
   */
  validateApiKey: async (provider: LLMProvider): Promise<boolean> => {
    try {
      const response = await apiClient.post<{ success: boolean; data: { isValid: boolean } }>(
        `/api/keys/${provider}/validate`
      );
      
      // Refresh the list to get updated validity status
      await get().fetchApiKeys();
      
      return response.data.data.isValid;
    } catch (error) {
      return false;
    }
  },
  
  /**
   * Fetch model settings for the current user
   * Validates: Requirements 21.1
   */
  fetchModelSettings: async () => {
    set({ settingsLoading: true, settingsError: null });
    
    try {
      const response = await apiClient.get<{ 
        success: boolean; 
        data: { 
          defaultModel: string | null;
          settings: Record<string, ModelSettings>;
        } 
      }>('/api/settings');
      
      set({ 
        modelSettings: response.data.data.settings || {},
        defaultModel: response.data.data.defaultModel,
        settingsLoading: false,
      });
    } catch (error) {
      // Settings might not exist yet, that's okay
      set({ 
        modelSettings: {},
        defaultModel: null,
        settingsLoading: false,
      });
    }
  },
  
  /**
   * Update settings for a specific model
   * Validates: Requirements 21.2
   */
  updateModelSettings: async (model: string, settings: ModelSettings): Promise<boolean> => {
    set({ settingsLoading: true, settingsError: null });
    
    try {
      await apiClient.put(`/api/settings/models/${encodeURIComponent(model)}`, settings);
      
      // Update local state
      set((state) => ({
        modelSettings: {
          ...state.modelSettings,
          [model]: settings,
        },
        settingsLoading: false,
      }));
      
      return true;
    } catch (error) {
      const message = getErrorMessage(error);
      set({ 
        settingsError: message,
        settingsLoading: false,
      });
      return false;
    }
  },
  
  /**
   * Set the default model
   * Validates: Requirements 21.3
   */
  setDefaultModel: async (model: string): Promise<boolean> => {
    set({ settingsLoading: true, settingsError: null });
    
    try {
      await apiClient.put('/api/settings/default-model', { model });
      
      set({ 
        defaultModel: model,
        settingsLoading: false,
      });
      
      return true;
    } catch (error) {
      const message = getErrorMessage(error);
      set({ 
        settingsError: message,
        settingsLoading: false,
      });
      return false;
    }
  },
  
  /**
   * Reset settings for a specific model to defaults
   * Validates: Requirements 21.4
   */
  resetModelSettings: async (model: string): Promise<boolean> => {
    set({ settingsLoading: true, settingsError: null });
    
    try {
      await apiClient.delete(`/api/settings/models/${encodeURIComponent(model)}`);
      
      // Update local state
      set((state) => {
        const newSettings = { ...state.modelSettings };
        delete newSettings[model];
        return {
          modelSettings: newSettings,
          settingsLoading: false,
        };
      });
      
      return true;
    } catch (error) {
      const message = getErrorMessage(error);
      set({ 
        settingsError: message,
        settingsLoading: false,
      });
      return false;
    }
  },
  
  /**
   * Check if a provider has an API key configured
   * Validates: Requirements 12.5
   */
  hasApiKey: (provider: LLMProvider): boolean => {
    return get().apiKeys.some((key) => key.provider === provider);
  },
}));

/**
 * Extract error message from error object
 */
function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as { response?: { data?: { message?: string } } };
    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }
  }
  return 'An error occurred. Please try again.';
}
